// styles.ts (FULL — fixed: drawerBody scroll + pinned footer)
// Token-driven to match the calendar's editorial look (Calendar.css): theme
// surfaces instead of hardcoded whites, dashed strong borders on callouts,
// mono uppercase micro-labels.

import React from "react";

export const styles: Record<string, any> = {
  root: {
    background: "var(--color-surface)",
    padding: 16,
    borderRadius: 12,
    boxShadow: "var(--shadow-sm)",
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
    flexShrink: 0,
  },
  search: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--color-border-strong)",
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    minWidth: 260,
  },
  error: {
    marginBottom: 12,
    color: "var(--color-danger)",
    background: "var(--color-danger-ghost)",
    border: "1px solid var(--color-danger-border)",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 13,
  },
  loading: { marginBottom: 12, color: "var(--color-muted)" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 12,
    alignContent: "start",
  },
  tile: {
    border: "1px solid var(--color-border)",
    borderRadius: 12,
    padding: 12,
    background: "var(--color-surface)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    transition: "transform 80ms ease, box-shadow 120ms ease",
  },
  tileHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  tileTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "var(--color-title)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "75%",
  },
  tileBody: {
    flex: 1,
    fontSize: 13,
    color: "var(--color-muted)",
    lineHeight: 1.45,
    minHeight: 44,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  tileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 6,
  },
  empty: {
    gridColumn: "1 / -1",
    padding: 24,
    textAlign: "center",
    color: "var(--color-muted)",
    border: "1px dashed var(--color-border-strong)",
    borderRadius: 12,
    background: "var(--color-subsurface)",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "color-mix(in srgb, var(--color-title) 35%, transparent)",
    zIndex: 40,
  },

  /* ===================== DRAWER (FIXED) ===================== */

  drawer: {
    position: "fixed",
    right: 0,
    top: 0,
    bottom: 0,
    width: "min(760px, 100%)",
    background: "var(--color-surface)",
    borderLeft: "1px solid var(--color-border)",
    boxShadow: "var(--shadow-lg)",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    zIndex: 50,
    color: "var(--color-ink)",

    overflow: "hidden", // ✅ key: body scrolls, footer stays visible
  },

  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    flex: "0 0 auto",
  },

  drawerBody: {
    flex: "1 1 auto",
    minHeight: 0, // ✅ critical for scroll in flex layouts
    overflowY: "auto",
    paddingRight: 4, // optional: avoids scrollbar overlay
  },

  drawerFooter: {
    flex: "0 0 auto",
    paddingTop: 10,
    borderTop: "1px solid var(--color-border)",
    background: "var(--color-surface)",
  },

  /* ===================== FORM ===================== */

  input: {
    padding: 10,
    borderRadius: 8,
    border: "1px solid var(--color-border-strong)",
    background: "var(--color-surface)",
    color: "var(--color-ink)",
    width: "100%",
  },
  // Editorial micro-label, matching the calendar's gutter/invoice labels.
  label: {
    fontFamily: "var(--font-mono)",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    margin: "8px 0 6px 2px",
    color: "var(--color-muted)",
  },
  resultBox: {
    background: "var(--color-subsurface)",
    border: "1px dashed var(--color-border-strong)",
    borderRadius: 8,
    padding: 12,
    whiteSpace: "normal",
    minHeight: 160,
    maxHeight: 360,
    overflow: "auto",
    fontFamily:
      "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
    lineHeight: 1.55,
    color: "var(--color-ink)",
  },
  mdPreview: { fontSize: 14 },
  preRaw: {
    margin: 0,
    whiteSpace: "pre-wrap",
    fontFamily: "var(--font-mono)",
  },
};

export function btnIcon(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    padding: 0,
    background: "var(--color-subsurface)",
    color: "var(--color-ink)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    cursor: "pointer",
  };
}

export function btnIconGhost(color = "var(--color-ink)"): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "var(--color-surface)",
    color,
    border: "1px solid var(--color-border)",
    cursor: "pointer",
  };
}

export function btnIconFilled(bg = "var(--color-brand)", color = "#fff"): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 34,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    cursor: "pointer",
  };
}
